<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :columns="displayColumns"
            :items="paginatedSuppliers"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export -->
                        <ExportSelect :options="exportOptions" @export-selected="handleExport" />
                    </div>

                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="handleCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('productSupplier.actions.create') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <a
                    @click.prevent="handleEdit(item)"
                    href="#"
                    class="ppv2-text-black poppinsRegular hover:ppv2-text-[#7D2DBD] ppv2-transition-colors ppv2-underline"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.name }}
                </a>
            </template>

            <!-- Column: Email -->
            <template #column-email="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.email || '-' }}
                </span>
            </template>

            <!-- Column: Phone -->
            <template #column-phone_number="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.phone_number || '-' }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div
                    :style="{ fontSize: normalizeFontSize(14) }"
                    class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                >
                    <button
                        @click="handleEdit(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('productSupplier.actions.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('productSupplier.actions.delete')"
                        @click="deleteSingleItem(item.id)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular textGray">
                    {{ selectedCount }} élément{{ selectedCount > 1 ? 's' : '' }} sélectionné{{ selectedCount > 1 ? 's' : '' }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Create Supplier Popup -->
        <CustomDialog v-model="showCreatePopup" :defaultTitle="$t('productSupplier.createPopup.title')" width="800px">
            <template slot="content">
                <SupplierForm @success="handleCreateSuccess" :token="token" />
            </template>
            <template slot="actions">
                <span></span>
            </template>
        </CustomDialog>

        <!-- Edit Supplier Popup -->
        <CustomDialog v-model="showEditPopup" :defaultTitle="$t('productSupplier.createPopup.title')" width="800px">
            <template slot="content">
                <SupplierForm v-if="showEditPopup" :editItem="editingItem" @success="handleEditSuccess" :token="token" />
            </template>
            <template slot="actions">
                <span></span>
            </template>
        </CustomDialog>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template slot="content">
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template slot="actions">
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('productSupplier.deletePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('productSupplier.deletePopup.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus, Trash2, SquarePen, LayoutGrid } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import Search from '../../Experience/components/Search.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import DataTable from '../../../Shared/DataTable.vue'
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseFileUpload from '../../../Shared/BaseFileUpload.vue'
import ExportSelect from '../../../Shared/ExportSelect.vue'
import SupplierForm from './SupplierForm.vue'
import { eventBus } from '../../../Customer/eventBus'
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    props: {
        userId: {
            type: [Number, String, null],
            required: false,
            default: null,
        },
        token: {
            type: String,
            default: '',
        },
    },
    name: 'SupplierTable',
    components: {
        Plus,
        SquarePen,
        Trash2,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
        LayoutGrid,
        BaseInput,
        BaseFileUpload,
        ExportSelect,
        SupplierForm,
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('suppliers_items_per_page', 10),
            selectedItems: [],
            selectAll: false,
            isLoading: false,
            error: null,
            orderBy: 'id',
            orderDirection: 'desc',
            tableColumns: [
                { key: 'name', label: this.$t('productSupplier.table.name'), sortable: true, type: 'text' },
                {
                    key: 'email',
                    label: this.$t('productSupplier.table.email'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'phone_number',
                    label: this.$t('productSupplier.table.phone'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'actions',
                    label: this.$t('productSupplier.table.actions'),
                    sortable: false,
                    type: 'actions',
                },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('productSupplier.deletePopup.title'),
            deletePopupDescription: '',
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null,
            suppliers: {
                data: [],
                total: 0,
            },
            showCreatePopup: false,
            showEditPopup: false,
            editingItem: null,
            exportOptions: [
                { value: 'csv', label: 'CSV' },
                { value: 'xls', label: 'Excel' },
            ],
        }
    },

    computed: {
        displayColumns() {
            return this.tableColumns
        },
        filteredSuppliers() {
            return this.suppliers.data || []
        },
        paginatedSuppliers() {
            return this.filteredSuppliers
        },
        totalPages() {
            return this.suppliers.total
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchSuppliers()
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handlePageChange(page) {
            this.currentPage = page
            this.fetchSuppliers()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchSuppliers()
        },
        handleSort(key) {
            if (this.orderBy === key) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = key
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchSuppliers()
        },

        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.paginatedSuppliers.length
        },

        toggleSelectAll() {
            const allIds = this.paginatedSuppliers.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },

        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return

            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('productSupplier.deletePopup.message')
            this.showDeletePopup = true
        },

        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('productSupplier.deletePopup.message')
            this.showDeletePopup = true
        },

        async confirmDelete() {
            try {
                let response
                if (this.deleteAction === 'multiple') {
                    response = await axios.post(
                        '/api/v3/supplier/bulk-delete',
                        {
                            ids: this.selectedItems,
                        },
                        {
                            headers: {
                                Authorization: `Bearer ${this.token}`,
                            },
                        }
                    )
                    this.selectedItems = []
                    this.selectAll = false
                } else {
                    response = await axios.delete(`/api/v3/supplier/delete/${this.itemToDelete}`, {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    })
                }

                this.$toast.success(response.data.message || 'Supprimé avec succès')
                await this.fetchSuppliers()
                this.$emit('refresh-stats')
            } catch (error) {
                const message =
                    error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : 'Erreur lors de la suppression'
                this.$toast.error(message)
            }

            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('suppliers_items_per_page', this.itemsPerPage)
            this.fetchSuppliers()
        },
        handleCreate() {
            this.showCreatePopup = true
        },
        handleCreateSuccess() {
            this.showCreatePopup = false
            this.fetchSuppliers()
            this.$emit('refresh-stats')
        },
        handleEdit(item) {
            this.editingItem = item
            this.showEditPopup = true
        },
        handleEditSuccess() {
            this.showEditPopup = false
            this.editingItem = null
            this.fetchSuppliers()
            this.$emit('refresh-stats')
        },

        async fetchSuppliers() {
            this.isLoading = true
            this.error = null

            try {
                let url = `/api/v3/supplier/list?currentPage=${this.currentPage}&search=${this.searchQuery}&itemsPerPage=${this.itemsPerPage}&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}`

                const res = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                const json = await res.json()
                this.suppliers = {
                    data: json.data || [],
                    total: json.last_page || 0,
                }
            } catch (err) {
                this.error = err.message
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
        async handleExport(format) {
            try {
                const response = await axios.get('/api/v3/supplier/export', {
                    params: {
                        format: format,
                        search: this.searchQuery,
                        orderBy: this.orderBy,
                        orderDirection: this.orderDirection,
                    },
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                const extension = format === 'xls' ? 'xlsx' : 'csv'
                link.setAttribute('download', `fournisseurs_${new Date().toISOString()}.${extension}`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Export error:', error)
                this.$toast.error("Erreur lors de l'export")
            }
        },
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.paginatedSuppliers.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },

        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },

        filteredSuppliers() {
            this.selectedItems = []
            this.selectAll = false
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
